import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Globe, ArrowRight, CircleNotch, CheckCircle, Eye, Copy, WarningCircle, MagnifyingGlass } from '@phosphor-icons/react';

const NamecheapProNewDeployment = () => {
  const [domains, setDomains] = useState([]);
  const [filteredDomains, setFilteredDomains] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingDomains, setLoadingDomains] = useState(false);
  
  const [hkAccounts, setHkAccounts] = useState([]);
  const [hgAccounts, setHgAccounts] = useState([]);
  const [source, setSource] = useState('namecheap');
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({ heroku_account_id: '', template_id: '' });
  const [customGitUrl, setCustomGitUrl] = useState("");
  const [useCustomGit, setUseCustomGit] = useState(false);
  const [noSource, setNoSource] = useState(false);
  const [selectedDomains, setSelectedDomains] = useState([]);
  
  const [deployments, setDeployments] = useState([]);
  const [deploying, setDeploying] = useState(false);
  const [serverIp, setServerIp] = useState("Loading...");

  useEffect(() => {
    fetchSetupData();
    fetchIp();
  }, []);

  const fetchIp = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/namecheap/ip`, { withCredentials: true });
      setServerIp(res.data.ip);
    } catch (e) {
      setServerIp("Unknown");
    }
  };

  useEffect(() => {
    setFilteredDomains(domains.filter(d => d.name.toLowerCase().includes(search.toLowerCase())));
  }, [search, domains]);

  const fetchSetupData = async () => {
    try {
      const [hkRes, tplRes, hgRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/heroku/accounts`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/heroku/templates`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/hostinger/accounts`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setHkAccounts(hkRes.data);
      setTemplates(tplRes.data);
      setHgAccounts(hgRes.data);
      
      setForm(f => ({
        ...f, 
        heroku_account_id: hkRes.data.length > 0 ? hkRes.data[0].id : '',
        template_id: tplRes.data.length > 0 ? tplRes.data[0].id : ''
      }));
    } catch (e) {
      toast.error("Failed to load Heroku accounts or templates");
    }
  };

  const switchSource = (s) => {
    if (s === source) return;
    setSource(s);
    setDomains([]);
    setSelectedDomains([]);
  };

  const fetchDomains = async () => {
    setLoadingDomains(true);
    try {
      const ep = source === 'hostinger' ? '/api/hostinger/domains' : '/api/namecheap/domains';
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${ep}`, { withCredentials: true });
      setDomains(res.data);
    } catch (e) {
      const msg = e.response?.data?.detail || (source === 'hostinger'
        ? "Failed to sync domains from Hostinger. Add a Hostinger account with a valid API token."
        : "Failed to sync domains from Namecheap. Check API whitelisting.");
      toast.error(msg);
    } finally {
      setLoadingDomains(false);
    }
  };
  const fetchNamecheap = fetchDomains;

  const toggleDomain = (domain) => {
    setSelectedDomains(prev => 
      prev.includes(domain) ? prev.filter(d => d !== domain) : [...prev, domain]
    );
  };

  const handleDeploy = async () => {
    if (!form.heroku_account_id) return toast.error("Select a Heroku Account.");
    if (!noSource) {
      if (!useCustomGit && !form.template_id) return toast.error("Select a Git Template or enter a custom Git URL.");
      if (useCustomGit && !customGitUrl) return toast.error("Enter a custom Git URL.");
    }
    if (selectedDomains.length === 0) return toast.error("Select at least one domain.");

    setDeploying(true);
    try {
      const payload = {
        heroku_account_id: form.heroku_account_id,
        domains: selectedDomains,
        source: source,
        hostinger_account_id: source === 'hostinger' ? (hgAccounts[0]?.id || null) : null
      };
      
      if (!noSource) {
        if (useCustomGit) {
          payload.custom_git_url = customGitUrl;
        } else {
          payload.template_id = form.template_id;
        }
      }

      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/namecheap-pro/deployments`, payload, { withCredentials: true });

      const initialDeps = res.data.map(d => ({ ...d, url: null, error: null }));
      setDeployments(initialDeps);

      initialDeps.forEach((dep, index) => {
        const poll = async () => {
          try {
            const pollRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/namecheap-pro/deployments`, { withCredentials: true });
            const liveDep = pollRes.data.find(d => d.id === dep.id);
            if (!liveDep) return;

            setDeployments(prev => {
              const arr = [...prev];
              if (liveDep.status === 'live') {
                arr[index] = { ...arr[index], status: 'live', url: liveDep.public_url };
              } else if (liveDep.status === 'failed') {
                arr[index] = { ...arr[index], status: 'failed', error: liveDep.error_msg || "Failed" };
              } else {
                arr[index] = { ...arr[index], status: liveDep.status };
              }
              return arr;
            });

            if (liveDep.status !== 'live' && liveDep.status !== 'failed') {
              setTimeout(poll, 15000); 
            }
          } catch (err) {
            console.error("Poll err");
          }
        };
        poll();
      });
    } catch (e) {
      const errorDetail = e.response?.data?.detail;
      const msg = Array.isArray(errorDetail) ? errorDetail[0]?.msg : (typeof errorDetail === 'string' ? errorDetail : "Failed to trigger deployments");
      toast.error(msg);
      setDeploying(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500 relative">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-4xl font-black text-[#00E5FF] mb-2">DOMAINS + HEROKU DEPLOYER</h1>
          <p className="text-slate-400 font-mono">Sync domains from Namecheap or Hostinger and deploy Git repos to Heroku seamlessly.</p>
        </div>
        <button 
          onClick={fetchDomains}
          disabled={loadingDomains}
          data-testid="sync-domains-btn"
          className="flex items-center gap-2 bg-transparent border border-[#00E5FF] text-[#00E5FF] px-4 py-2 font-bold hover:bg-[#00E5FF] hover:text-black transition-colors disabled:opacity-50"
        >
          {loadingDomains ? <CircleNotch size={20} className="animate-spin" /> : <Globe size={20} />}
          SYNC {source === 'hostinger' ? 'HOSTINGER' : 'NAMECHEAP'}
        </button>
      </div>

      {/* Domain source selector */}
      <div className="flex items-center gap-3 max-w-5xl" data-testid="source-selector">
        <span className="text-xs uppercase tracking-widest text-slate-500 font-bold">Domain Source</span>
        {['namecheap', 'hostinger'].map(s => (
          <button key={s} onClick={() => switchSource(s)} data-testid={`source-${s}`}
            className={`px-4 py-2 text-sm font-bold uppercase tracking-wider border transition-colors ${source === s ? 'bg-[#00E5FF] text-black border-[#00E5FF]' : 'bg-transparent text-slate-300 border-white/15 hover:border-[#00E5FF]/60'}`}>
            {s}
          </button>
        ))}
        {source === 'hostinger' && hgAccounts.length === 0 && (
          <span className="text-xs text-amber-400 flex items-center gap-1"><WarningCircle size={14} /> No Hostinger account — add one under "Hostinger Accounts".</span>
        )}
      </div>

      {/* Server IP banner — only relevant for Namecheap (Hostinger auth is token-based, no IP whitelist) */}
      {source === 'namecheap' && (
      <div data-testid="namecheap-server-ip-banner" className="bg-[#00E5FF]/5 border border-[#00E5FF]/40 shadow-[0_0_20px_rgba(0,229,255,0.08)] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 max-w-5xl">
        <div className="flex items-center gap-3">
          <span className="relative flex h-3 w-3 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00E5FF] opacity-60"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-[#00E5FF]"></span>
          </span>
          <div>
            <p className="text-[11px] uppercase tracking-widest text-[#00E5FF] font-bold">Server IP · Namecheap Whitelist</p>
            <p className="text-[11px] text-slate-400">Add this IP under Namecheap → Profile → Tools → API Access → Whitelisted IPs</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <code data-testid="namecheap-server-ip" className="bg-[#0A0B0E] border border-[#00E5FF]/30 px-4 py-2 text-lg font-mono font-black text-[#00E5FF] select-all tracking-wide">
            {serverIp}
          </code>
          <button
            data-testid="copy-server-ip-btn"
            onClick={() => { navigator.clipboard.writeText(serverIp); toast.success("Server IP copied!"); }}
            disabled={serverIp === 'Loading...' || serverIp === 'Unknown'}
            className="flex items-center gap-1 border border-[#00E5FF] text-[#00E5FF] px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#00E5FF] hover:text-black transition-colors disabled:opacity-40"
          >
            <Copy size={15} /> Copy
          </button>
        </div>
      </div>
      )}

      <div className="bg-card border border-[#00E5FF]/30 p-8 max-w-5xl space-y-8 shadow-[0_0_15px_rgba(0,229,255,0.05)]">
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2 border-b border-white/10 pb-6">
            <label className="text-xs uppercase tracking-widest text-[#8b60ed] font-bold block mb-2">1. Compute Target (Heroku)</label>
            <select 
              value={form.heroku_account_id} 
              onChange={(e) => setForm({...form, heroku_account_id: e.target.value})}
              className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white focus:outline-none focus:border-[#8b60ed] appearance-none rounded-none"
            >
              <option value="">-- Choose Heroku Account --</option>
              {hkAccounts.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
            <p className="text-slate-500 text-xs">PHP Containers will be spun up here.</p>
          </div>
          
          <div className="space-y-2 border-b border-white/10 pb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="text-xs uppercase tracking-widest text-slate-300 font-bold block">2. Source (Git Template)</label>
              {!noSource && (
                <button 
                  data-testid="toggle-custom-git-btn"
                  onClick={() => setUseCustomGit(!useCustomGit)}
                  className="text-xs text-[#00E5FF] hover:underline"
                >
                  {useCustomGit ? "Use Saved Template" : "Use Custom URL"}
                </button>
              )}
            </div>

            <label className="flex items-center gap-2 mb-3 cursor-pointer select-none" data-testid="no-source-toggle">
              <input
                type="checkbox"
                checked={noSource}
                onChange={(e) => setNoSource(e.target.checked)}
                className="accent-[#00E5FF] w-4 h-4"
              />
              <span className="text-xs text-slate-300">No source — create empty app (I'll push files to Heroku manually)</span>
            </label>
            
            {noSource ? (
              <div className="w-full bg-[#0A0B0E] border border-dashed border-[#00E5FF]/40 p-3 text-slate-400 text-xs font-mono">
                Empty Heroku app will be created + domain bound + Namecheap DNS set. Connect the app to GitHub in Heroku afterward.
              </div>
            ) : useCustomGit ? (
              <input 
                data-testid="custom-git-input"
                type="text" 
                value={customGitUrl}
                onChange={(e) => setCustomGitUrl(e.target.value.trim())}
                placeholder="https://github.com/user/repo"
                className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white focus:outline-none focus:border-[#00E5FF] font-mono text-sm"
              />
            ) : (
              <select 
                data-testid="template-select"
                value={form.template_id} 
                onChange={(e) => setForm({...form, template_id: e.target.value})}
                className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white focus:outline-none focus:border-white/30 appearance-none rounded-none"
              >
                <option value="">-- Choose Git Repo --</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            )}
            <p className="text-slate-500 text-xs">Repository to deploy.</p>
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-4">
            <label className="text-xs uppercase tracking-widest text-[#00E5FF] font-bold block">3. Select Domains to Deploy</label>
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-2.5 text-slate-400" />
              <input 
                type="text" 
                placeholder="Search..." 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-[#0A0B0E] border border-white/10 pl-9 pr-3 py-2 text-sm text-white focus:outline-none focus:border-[#00E5FF] w-64"
              />
            </div>
          </div>
          
          <div className="border border-white/10 bg-[#0A0B0E] h-96 overflow-y-auto">
            {loadingDomains ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-500">
                <CircleNotch size={32} className="animate-spin mb-2" />
                <p>Fetching domains from {source === 'hostinger' ? 'Hostinger' : 'Namecheap'}...</p>
              </div>
            ) : domains.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-500">
                <p>Click "Sync {source === 'hostinger' ? 'Hostinger' : 'Namecheap'}" to load domains.</p>
              </div>
            ) : (
              <table className="w-full text-left text-sm border-collapse">
                <tbody>
                  {filteredDomains.map(domain => {
                    const isSelected = selectedDomains.includes(domain.name);
                    return (
                      <tr 
                        key={domain.id} 
                        onClick={() => toggleDomain(domain.name)}
                        className={`border-b border-white/5 cursor-pointer transition-colors ${isSelected ? 'bg-[#00E5FF]/10' : 'hover:bg-white/5'}`}
                      >
                        <td className="p-4 w-12">
                          <div className={`w-5 h-5 border flex items-center justify-center transition-colors ${isSelected ? 'bg-[#00E5FF] border-[#00E5FF]' : 'border-white/20'}`}>
                            {isSelected && <CheckCircle size={16} weight="bold" className="text-black" />}
                          </div>
                        </td>
                        <td className={`p-4 font-mono font-bold ${isSelected ? 'text-[#00E5FF]' : 'text-white'}`}>
                          {domain.name}
                        </td>
                        <td className="p-4 text-xs text-slate-500">Exp: {domain.expires}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <button 
          data-testid="deploy-domains-btn"
          onClick={handleDeploy}
          disabled={deploying || !form.heroku_account_id || selectedDomains.length === 0}
          className="w-full bg-[#00E5FF] text-black px-8 py-4 font-black uppercase tracking-wider hover:bg-[#00B8CC] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          DEPLOY {selectedDomains.length} DOMAINS TO HEROKU <ArrowRight size={20} />
        </button>
      </div>

      {deployments.length > 0 && (
        <div className="bg-card border border-[#00E5FF]/30 overflow-hidden mt-8">
          <div className="p-4 border-b border-white/10 bg-black/20">
            <h3 className="font-bold uppercase tracking-wider text-[#00E5FF]">Deployment Results</h3>
            <p className="text-xs text-slate-500 mt-1">Note: Namecheap DNS propagation and Heroku SSL provisioning can take up to 20 minutes.</p>
          </div>
          <table className="w-full text-left text-sm border-collapse">
            <tbody>
              {deployments.map((dep, i) => (
                <tr key={i} className="border-b border-white/10 hover:bg-white/5">
                  <td className="p-4 font-mono font-bold w-1/4">
                    <Globe className="inline mr-2 text-[#00E5FF]" />
                    {dep.domain}
                  </td>
                  <td className="p-4 w-1/4">
                    {dep.status === 'building' ? <span className="text-[#00E5FF] text-xs font-bold animate-pulse flex items-center gap-1"><CircleNotch className="animate-spin" /> Provisioning...</span> :
                     dep.status === 'live' ? <span className="text-[#39FF14] text-xs font-bold flex items-center gap-1"><CheckCircle /> SSL Secured & Live</span> :
                     <span className="text-red-500 text-xs font-bold flex items-center gap-1"><WarningCircle /> Failed</span>}
                  </td>
                  <td className="p-4 font-mono">
                    {dep.status === 'live' && dep.url && (
                      <div className="flex items-center gap-2">
                        <div className="bg-[#0A0B0E] border border-white/10 px-2 py-1 text-xs text-slate-400 font-mono truncate w-64 select-all" title={dep.url}>
                          {dep.url}
                        </div>
                        <button onClick={() => { navigator.clipboard.writeText(dep.url); toast.success("Copied!"); }} className="text-slate-400 hover:text-white transition-colors p-1"><Copy size={16} /></button>
                        <a href={dep.url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#00E5FF] transition-colors p-1"><Eye size={16} /></a>
                      </div>
                    )}
                    {dep.status === 'failed' && <span className="text-red-500 text-xs truncate max-w-[200px] block" title={dep.error_msg}>{dep.error_msg}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NamecheapProNewDeployment;